/* eslint-disable jsx-a11y/media-has-caption */
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectVideoCallCredentials } from '@brands/store/selectors/videoCallCredentials';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { LocalVideoTrack, RemoteVideoTrack } from 'twilio-video';

import { useVideoContext, useVideoTrackDimensions } from '../../hooks';
import styles from './styles.module.scss';
import { useWindowSize } from '@brands/hooks';

interface VideoTrackProps {
  track: LocalVideoTrack | RemoteVideoTrack;
  isWaitingRoom?: boolean;
}

const VideoTrack: React.FC<VideoTrackProps> = ({ track, isWaitingRoom }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useWindowSize().width < 992;
  const { userInfo } = useAppSelector(selectAuth);
  const {
    is_visible: isVisible,
    mobileModalMode,
    mobileModalHeight,
    hasJoined,
  } = useAppSelector(selectVideoCallCredentials);
  const { room, isSideBySide } = useVideoContext();
  const dimensions = useVideoTrackDimensions(track);
  const isPortrait = (dimensions?.height ?? 0) > (dimensions?.width ?? 0);
  const remoteParticipantsSize = room?.participants.size || 0;

  const isSafari = useCallback(() => {
    const ua = navigator.userAgent.toLowerCase();
    return ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1;
  }, []);

  useEffect(() => {
    if (videoRef.current && track) {
      track.detach();
      track.attach(videoRef.current);

      if (track.kind === 'video' && track instanceof LocalVideoTrack) {
        if (!track.isEnabled) {
          track.enable();
        }
        videoRef.current.muted = false;
      }
    }

    return () => {
      if (videoRef.current && track) {
        track.detach(videoRef.current);
        videoRef.current.srcObject = null;
      }
    };
  }, [track]);

  useEffect(() => {
    if (videoRef.current && track) {
      track.detach();
      track.attach(videoRef.current);

      if (track.kind === 'video' && track instanceof LocalVideoTrack && track.isMuted && isVisible) {
        room?.refreshInactiveMedia();
      }
    }
  }, [track, isVisible, room]);

  useEffect(() => {
    if (track.kind === 'video' && track instanceof LocalVideoTrack && track.isMuted && isVisible) {
      room?.refreshInactiveMedia();
    }
  }, [track, isVisible, room]);

  const isRemoteVideoTrack = (track as RemoteVideoTrack).sid !== undefined;
  const isPatient = userInfo?.role?.name === UserRoleName.Patient;
  const isScreenShare = track.name.toLowerCase().includes('screen');

  const videoHeight = useMemo(() => {
    if (isPatient) {
      return '100%';
    }

    if (isWaitingRoom && !isMobile) {
      return '270px';
    }

    if (isWaitingRoom && isMobile) {
      return '100%';
    }

    if (!isMobile) {
      return '100%';
    }

    if (mobileModalMode === 'expanded') {
      const calculatedHeight = hasJoined ? 'fit-content' : mobileModalHeight - 370;

      return `${Math.max(calculatedHeight, 0)}px`;
    }

    return '100%';
  }, [isPatient, isWaitingRoom, isMobile, mobileModalHeight, mobileModalMode, hasJoined]);

  const style = {
    objectFit: (!isScreenShare && !isPatient && isMobile ? 'cover' : 'contain') as 'cover' | 'contain',
    maxWidth: '100%',
    maxHeight: '100%',
    width:
      userInfo.role.name === UserRoleName.Patient
        ? (isMobile && !isRemoteVideoTrack && !isWaitingRoom && remoteParticipantsSize > 0) ||
          (isSafari() && !isWaitingRoom) ||
          isPortrait
          ? '100%'
          : '100%'
        : '100%',
    height: videoHeight,
  };

  if (!track) {
    return null;
  }

  return (
    <video
      ref={videoRef}
      playsInline
      autoPlay
      onClick={() => {
        if (videoRef.current && videoRef.current.paused) {
          videoRef.current.play();
        }
      }}
      className={`fs-exclude ${isSideBySide ? styles.video : ''}`}
      id={isWaitingRoom ? 'preview' : track.name}
      controls={false}
      style={style}
    />
  );
};

VideoTrack.defaultProps = {
  isWaitingRoom: false,
};

export default VideoTrack;
